import { useCallback, useMemo, useState } from "react";
import { memo } from "react";
import "./FiltroViagens.css";
import Separator from "../../../../../core/components/Separator.tsx";
import ModalFiltrosViagens from "../ModalFiltroViagem/ModalFiltrosViagens.tsx";
import IconButton from "../../../../../core/components/IconButton.tsx";
import FiltroData from "../../../../../core/data-entry/FiltroData.tsx";
import {
	FiltroEmbarqueCompleto,
	FiltroEmbarqueCompletoEnum,
} from "../../../../../models/FiltroEmbarqueCompleto.ts";
import { formatDate } from "../../../../../core/utils/formatDate.ts";
import ModalConfigTabela from "../../../../../components/ModalConfigTabela/ModalConfigTabela.tsx";
import { useProfile } from "../../../../../contexts/ProfileContext.tsx";
import { Tooltip } from "@mui/material";
import { useFiltro } from "../../../../../contexts/FiltroContext.tsx";
import BarraSearch from "../../../../../core/data-entry/BarraSearch.tsx";
import Counter from "../../../../../core/data-display/Counter.tsx";
import { motion } from "framer-motion";
import RenderCsvLink, {
	ClasseCsv,
} from "../../../../../core/components/RenderCsvLink.tsx";

interface FiltroViagensProps {
	onClearFiltros: () => void;
	buscarViagens: () => void;
	clearToasts?: () => void;
	buscarViagensClipboard?: (filtros: FiltroEmbarqueCompleto) => void;
	listaFormatada?: any[];
	showConfig?: boolean;
	showDiariaExcedida?: boolean;
	disabled?: boolean;
}

function FiltroViagens({
	buscarViagens,
	clearToasts,
	listaFormatada,
	onClearFiltros,
	showConfig = true,
	showDiariaExcedida = true,
	buscarViagensClipboard,
	disabled = false,
}: FiltroViagensProps) {
	const {
		filtroEmbarque,
		setFiltro,
		qtFiltrosAplicados,
		copyFiltrosToClipboard,
		aplicaFiltrosCopiado,
	} = useFiltro();
	const { tabelaUsuario } = useProfile();

	const [openConfig, setOpenConfig] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const date = useMemo(() => new Date(), []);

	const valuePrevisaoEmbarqueInicio = useMemo(() => {
		return (
			formatDate(filtroEmbarque.previsaoEmbarqueInicio) ||
			new Date(date.getFullYear(), date.getMonth(), 1)
				.toISOString()
				.split("T")[0]
		);
	}, [filtroEmbarque.previsaoEmbarqueInicio, date]);

	const valuePrevisaoEmbarqueFim = useMemo(() => {
		return (
			formatDate(filtroEmbarque.previsaoEmbarqueFim) ||
			new Date(date.getFullYear(), date.getMonth() + 1, 0)
				.toISOString()
				.split("T")[0]
		);
	}, [filtroEmbarque.previsaoEmbarqueFim, date]);

	const abrirModal = useCallback(() => {
		if (!isOpen) {
			setIsOpen(true);
			clearToasts?.();
		}
	}, [isOpen, clearToasts]);

	const closeModal = useCallback(() => {
		setIsOpen(false);
	}, []);

	const handleCloseConfig = useCallback(() => {
		setOpenConfig(false);
	}, []);

	const handleBuscarViagens = useCallback(() => {
		buscarViagens();
		setIsOpen(false);
	}, [buscarViagens]);

	return (
		<div className="filtro-viagens-container">
			<div
				style={{
					width: "150px",
				}}
			/>
			<div className="filtro-viagens-row">
				<div className="filtro-data-container">
					<FiltroData
						title="Início"
						value={valuePrevisaoEmbarqueInicio}
						disabled={disabled}
						onChange={(value) =>
							setFiltro(
								FiltroEmbarqueCompletoEnum.PrevisaoEmbarqueInicio,
								formatDate(value)
							)
						}
					/>
					<div className="filtro-data-divider" />
					<FiltroData
						title="Fim"
						titleFrente={false}
						value={valuePrevisaoEmbarqueFim}
						disabled={disabled}
						onChange={(value) =>
							setFiltro(
								FiltroEmbarqueCompletoEnum.PrevisaoEmbarqueFim,
								formatDate(value)
							)
						}
					/>
				</div>
				<Separator size="md" direction="horizontal" />
				<Tooltip title="Copiar Filtros">
					<span>
						<IconButton
							icon="Copy"
							disabled={disabled}
							onClick={() => copyFiltrosToClipboard()}
							size="xl"
							theme="dark"
							backgroundColor="#FFFFFF"
							hoverColor="#CCC"
						/>
					</span>
				</Tooltip>
				<Separator direction="horizontal" size="xs" />
				<div
					onClick={abrirModal}
					className="filtro-viagens-multiplos-filtros-container"
				>
					<Separator direction="horizontal" size="xs" />
					<b>Múltiplos Filtros</b>
					<Separator direction="horizontal" size="xs" />
					<Counter number={qtFiltrosAplicados} />
				</div>
				<Separator direction="horizontal" size="xs" />
				<Tooltip title={"Aplicar Filtros Copiados"}>
					<span>
						<IconButton
							disabled={disabled}
							icon="ClipboardCheck"
							onClick={() => aplicaFiltrosCopiado({ buscarViagensClipboard })}
							size="xl"
							theme="dark"
							backgroundColor="#FFFFFF"
							hoverColor="#CCC"
						/>
					</span>
				</Tooltip>
				<Separator size="md" direction="horizontal" />
				<BarraSearch
					buscarSearch={buscarViagens}
					clearSearch={onClearFiltros}
					value={filtroEmbarque.valorGeral || ""}
					onChange={(value) => {
						setFiltro(FiltroEmbarqueCompletoEnum.ValorGeral, value);
					}}
				/>
			</div>
			<div className="filtro-viagens-icons">
				{listaFormatada && (
					<RenderCsvLink
						disabled={disabled}
						classe={ClasseCsv.idEmbarque}
						lista={listaFormatada}
					/>
				)}
				<Separator size="lg" direction="horizontal" />
				{showConfig && (
					<Tooltip title={<p>Abrir configurações colunas</p>}>
						<motion.div
							initial={{ rotate: 0 }}
							whileHover={{ rotate: 90 }}
							transition={{ duration: 0.2, ease: "easeInOut" }}
							style={{
								transformOrigin: "50% 50%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							className="select-field-chevron"
						>
							<IconButton
								disabled={disabled}
								icon="Settings"
								size="2xl"
								onClick={() => setOpenConfig(true)}
								theme="dark"
								backgroundColor="#FFFFFF"
								hoverColor="#CCC"
							/>
						</motion.div>
					</Tooltip>
				)}
			</div>
			<ModalFiltrosViagens
				isOpen={isOpen}
				onClose={closeModal}
				buscarViagens={handleBuscarViagens}
				onClearFiltros={onClearFiltros}
				showDiariaExcedida={showDiariaExcedida}
			/>
			<ModalConfigTabela
				titulo="Colunas Visíveis"
				tituloConfirmar="OK"
				open={openConfig}
				onClose={handleCloseConfig}
				checks={tabelaUsuario.Embarque}
				nome={"embarque"}
			/>
		</div>
	);
}

export default memo(FiltroViagens);
